import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import FileRow from "./FileRow";
import {
  FilesFormFields,
  FileFormFields,
  filesValidationSchema,
} from "./filesValidationSchema";
import {
  CompanyProfileRequest,
  CompanyProfileResponse,
  FileType,
} from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./filesForm.scss";

interface FilesFormProps {
  profile: CompanyProfileResponse;
  setIsDirty: (isDirty: boolean) => void;
}

const emptyFile: FileFormFields = {
  fileURL: "",
  name: "",
  description: "",
};

/**
 * The (tax-document) Files wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon +
 * React Hook Form's `useFieldArray` (Task 10, Profile/Company flow) — the SAME
 * Addresses/BankingInformation/Certifications (Tasks 8-9) rewrite shape: ONE `files` array,
 * scoped to `FileType.TAX_DOCUMENTS` and seeded from `profile.files`, editable in place via
 * `useFieldArray` (`append`/`remove`), submitted whole. Real behaviour change from the pre-Carbon
 * form (which only ever let you ADD new rows or remove an unsaved one), safe for the identical
 * reason Task 8/9 already established for their own lists: `CompanyService#updateCompanyFiles`
 * (backend) already fully REPLACES the company's ENTIRE `files` list on every save
 * (`company.getFiles().clear()` then re-adds from the request) — the pre-Carbon form's
 * "existing rows are read-only" behaviour was a FRONTEND-only restriction with no backend
 * counterpart.
 *
 * `CompanyService#updateCompanyFiles` replaces EVERY file type, not just tax documents — so
 * `onSubmit` reproduces the pre-Carbon form's own `otherFiles` merge exactly: files of every OTHER
 * type (`FileType.PRODUCT_CATALOG`/`COMPANY_PRESENTATION`/etc., owned by
 * `BasicInformationForm.tsx`) are read straight from `profile.files` and concatenated with this
 * step's own `TAX_DOCUMENTS` rows before sending — never dropped just because this sub-form
 * doesn't render them.
 *
 * `onSubmit` also spreads `{...profile, ...}` (not just `{files}`), same as every other wizard
 * sub-form — `taxId` is `@NotBlank` on the backend (Task 2), and every independent save must keep
 * carrying it.
 */
const FilesForm = ({ profile, setIsDirty }: FilesFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const otherFiles = useMemo(
    () =>
      (profile.files || []).filter(
        (file) => file.type !== FileType.TAX_DOCUMENTS
      ),
    [profile]
  );

  const defaultValues: FilesFormFields = useMemo(
    () => ({
      files: (profile.files || [])
        .filter((file) => file.type === FileType.TAX_DOCUMENTS)
        .map((file) => ({
          fileURL: file.fileURL ?? "",
          name: file.name ?? "",
          description: file.description ?? "",
        })),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<FilesFormFields>({
    resolver: yupResolver(filesValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as AddressesForm.tsx/
  // BasicInformationForm.tsx — see those files' own docstrings/comments for the full rationale.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "files",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: FilesFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      files: [
        ...otherFiles,
        ...data.files.map((file) => ({
          ...file,
          type: FileType.TAX_DOCUMENTS,
        })),
      ],
    } as CompanyProfileRequest);
  };

  return (
    <div className="files-form">
      <div className="files-form__header">
        <h3>{t("companyProfile.taxDocuments")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyFile)}
        >
          {t("companyProfile.addTaxDocument")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="files-form__form"
        >
          {fields.length > 0 && (
            <div className="files-form__rows">
              {fields.map((field, index) => (
                <FileRow
                  key={field.id}
                  index={index}
                  control={control}
                  setValue={setValue}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="files-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default FilesForm;
